import { ReactNode } from "react";
import { Box, Stack, useTheme } from "@mui/material";
import { Typography } from "@foodpilot/foods";

export type SectionHeaderProps = {
    /** Progress is optional for section groups that do not expose a staged flow. */
    progress?: {
        /** 1-based index in the mounted section list. */
        current: number;
        /** Total number of mounted sections. */
        total: number;
    };
    title: string;
    subtitle?: string;
    /**
     * Optional element rendered top-right of the header, used by impact-bearing sections
     * (Recipe, Packaging, Transformation, Distribution, Consumption) to surface a
     * placeholder per-section score badge per Figma `12819:67407`.
     */
    rightSlot?: ReactNode;
    /** Empty Figma score tags align to the header top; populated tags stay centered. */
    rightSlotAlignment?: "start" | "center";
    /** Map-preview headers can opt out of the divider while standard form sections keep it. */
    showDivider?: boolean;
};

/**
 * Standardized section header per Figma `12819:67407` ("Product creation - Filled").
 *
 * Renders an optional `N/T ·` prefix, a title, subtitle, right-side slot and divider.
 * Progress is supplied by the owning page from its mounted section list.
 */
export const SectionHeader = (props: SectionHeaderProps) => {
    const { progress, title, subtitle, rightSlot, rightSlotAlignment = "center", showDivider = true } = props;
    const theme = useTheme();
    const accessibleTitle = progress ? `${progress.current}/${progress.total} ${title}` : title;

    return (
        <Box
            sx={{
                backgroundColor: theme.custom.grey[200],
                borderTopLeftRadius: theme.spacing(1.5),
                borderTopRightRadius: theme.spacing(1.5),
                position: "relative",
            }}
        >
            <Stack
                direction="row"
                alignItems={rightSlotAlignment === "start" ? "flex-start" : "center"}
                sx={{ gap: theme.spacing(2), padding: theme.spacing(3) }}
            >
                <Stack sx={{ flex: 1, gap: theme.spacing(0), minWidth: 0 }}>
                    <Typography component="h2" variant="huge-semibold" aria-label={accessibleTitle}>
                        {progress && (
                            <>
                                <Box component="span">{`${progress.current}/${progress.total}`}</Box>
                                <Box
                                    component="span"
                                    sx={{
                                        color: theme.custom.grey[2000],
                                        margin: `0 ${theme.spacing(1)}`,
                                    }}
                                    aria-hidden
                                >
                                    {"•"}
                                </Box>
                            </>
                        )}
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                            {subtitle}
                        </Typography>
                    )}
                </Stack>
                {rightSlot && <Box sx={{ flexShrink: 0 }}>{rightSlot}</Box>}
            </Stack>
            {showDivider && (
                <Box
                    aria-hidden
                    sx={{
                        borderBottomColor: theme.custom.strokes.containerDivider.color,
                        borderBottomStyle: theme.custom.strokes.containerDivider.style,
                        borderBottomWidth: theme.custom.strokes.containerDivider.width,
                        bottom: 0,
                        left: 0,
                        pointerEvents: "none",
                        position: "absolute",
                        right: 0,
                    }}
                />
            )}
        </Box>
    );
};
